// assets/eos-kisisellestirme-yolu.js
import { Component } from '@theme/component';

/**
 * @typedef {object} EosKisisellestirmeYoluRefs
 * @property {HTMLInputElement[]} [secim] - One radio per personalisation kind.
 * @property {HTMLElement} [sahne] - The stage wrapper.
 */

/** Below this width the stage sits under the option cards. */
const STACKED_QUERY = '(max-width: 760px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Home page chooser for İsim / Harf / Zodyak.
 *
 * Progressive enhancement only. The stage already swaps with CSS
 * (`:has(:checked)`) and every product link is a plain anchor, so nothing here
 * is required for the section to work. This module mirrors the checked radio
 * into `is-active` / `is-selected` classes, which is what browsers without
 * `:has` fall back to, and on the stacked mobile layout brings the stage into
 * view when a choice changes it off screen.
 *
 * No IntersectionObserver or requestAnimationFrame: the change event is enough.
 *
 * @extends Component<EosKisisellestirmeYoluRefs>
 */
class EosKisisellestirmeYolu extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  handleChange() {
    this.#sync();
    this.#revealStage();
  }

  #sync() {
    const options = this.#options;
    const checked = options.find((option) => option.checked) ?? options[0];
    if (!checked) return;

    const active = checked.value;

    for (const option of options) {
      option.closest('.eos-yol__secenek')?.classList.toggle('is-selected', option === checked);
    }

    for (const panel of this.querySelectorAll('[data-eos-yol-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.classList.toggle('is-active', panel.dataset.eosYolPanel === active);
    }
  }

  /**
   * Scrolls the stage to the nearest edge of the visible area, only on the
   * stacked layout and only when part of it is hidden. Horizon scrolls inside
   * `.page-wrapper`, so its rect bounds the visible area when present.
   */
  #revealStage() {
    const stage = this.refs.sahne;
    if (!(stage instanceof HTMLElement)) return;
    if (!window.matchMedia(STACKED_QUERY).matches) return;

    const { top: viewTop, bottom: viewBottom } = this.#visibleBounds();
    if (viewBottom <= viewTop) return;

    const rect = stage.getBoundingClientRect();
    if (rect.top >= viewTop && rect.bottom <= viewBottom) return;

    const reduced = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    stage.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }

  /** @returns {{ top: number, bottom: number }} */
  #visibleBounds() {
    const viewportHeight = window.visualViewport?.height || document.documentElement.clientHeight || 0;
    const wrapper = this.closest('.page-wrapper');
    if (!(wrapper instanceof HTMLElement)) return { top: 0, bottom: viewportHeight };

    const rect = wrapper.getBoundingClientRect();
    const top = Math.max(rect.top, 0);
    const bottom = viewportHeight > 0 ? Math.min(rect.bottom, viewportHeight) : rect.bottom;
    return { top, bottom };
  }

  /** @returns {HTMLInputElement[]} */
  get #options() {
    const refs = this.refs.secim;
    if (!refs) return [];
    return Array.isArray(refs) ? refs : [refs];
  }
}

if (!customElements.get('eos-kisisellestirme-yolu')) {
  customElements.define('eos-kisisellestirme-yolu', EosKisisellestirmeYolu);
}
